import { lazy, Suspense, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    chipClass, cx, errorClass, fieldClass, helpClass, IconCalendar, IconClose, labelClass, Spinner, useIsPhone, useModal, popoverClass,
} from '@timely/ui';
import { presetDay, presetLabel } from '@timely/domain';
import { useFormatters } from '../book/useFormatters';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';

/* The grid of days is its own chunk, fetched when a date field is first
   hovered or focused, so no screen pays for the calendar until it is wanted. */
const DayGrid = lazy(() => import('./DayGrid'));
const preloadCalendar = () => { void import('./DayGrid'); };

/**
 * A calendar day, asked for (DESIGN-SYSTEM §8).
 *
 * The one place a form asks for a day. A field-shaped button shows the chosen
 * day in words; pressing it opens a calendar beside the field (a sheet from
 * the bottom on a phone). The calendar is react-day-picker: a real grid with
 * the full keyboard — arrows by day and week, Page Up/Down by month, Home and
 * End for the week — and a spoken date on every day. Choosing a day closes it:
 * there is no second confirmation. Esc or a press outside closes it, and focus
 * goes back to the field.
 *
 * The calendar is drawn outside the page's flow (a portal, fixed to the
 * field), so no card, table or scrolling dialog can clip it; inside a dialog it
 * stays within the dialog, so the dialog's own keyboard handling still holds.
 * It takes the keyboard while open, as the innermost layer.
 *
 * The common gaps are one press under the field, counted from the company's
 * day. The value is always a plain `YYYY-MM-DD` calendar day, never a moment.
 */
export function DateField({
    id, label, value, onChange, error, help, disabled, presets, notBefore, clearable = false,
}: {
    id: string;
    label: string;
    /** `YYYY-MM-DD`, or '' for none. */
    value: string;
    onChange: (value: string) => void;
    error?: string | undefined;
    help?: string;
    disabled?: boolean;
    /** One-press gaps under the field, in days from today: `[1, 3, 7]`. */
    presets?: readonly number[];
    /** The earliest day that may be chosen; days before it are shown but not offered. */
    notBefore?: string | undefined;
    /** Offer a way back to no date. */
    clearable?: boolean;
}) {
    const { today } = useFormatters();
    const { workspace } = useActiveWorkspace();
    const [open, setOpen] = useState(false);
    const trigger = useRef<HTMLButtonElement>(null);

    // After the calendar has gone: a browser that does not focus a pressed
    // button (Safari) leaves nothing for the layer to hand focus back to.
    const refocus = () => { window.setTimeout(() => trigger.current?.focus(), 0); };

    const selected = value ? fromIso(value) : undefined;
    const spoken = selected ? longDay(selected, workspace.locale) : null;
    const describedBy = [error ? `${id}-error` : null, help ? `${id}-help` : null].filter(Boolean).join(' ') || undefined;

    return (
        <div>
            <label htmlFor={id} className={labelClass}>{label}</label>
            <div className="relative">
                <button
                    ref={trigger}
                    id={id}
                    type="button"
                    disabled={disabled}
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={describedBy}
                    onClick={() => setOpen(o => !o)}
                    onPointerEnter={preloadCalendar}
                    onFocus={preloadCalendar}
                    className={cx(
                        fieldClass({ invalid: Boolean(error), className: 'w-full flex items-center gap-2 text-left' }),
                        clearable && value && 'pr-9',
                    )}
                >
                    <span className="text-label-3 flex-none"><IconCalendar /></span>
                    {spoken
                        ? <span className="truncate">{spoken}</span>
                        : <span className="truncate text-label-3">Choose a day</span>}
                </button>
                {clearable && value && !disabled && (
                    <button
                        type="button"
                        aria-label={`Clear ${label.toLowerCase()}`}
                        title="Clear"
                        onClick={() => { onChange(''); trigger.current?.focus(); }}
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 grid place-items-center rounded-sm text-label-3 hover:text-label hover:bg-hover"
                    >
                        <IconClose size={14} />
                    </button>
                )}
            </div>

            {presets && presets.length > 0 && (
                <div role="group" aria-label={`${label}: quick choices`} className="flex flex-wrap gap-1.5 mt-2">
                    {presets.map(days => {
                        const day = presetDay(days, today);
                        const pressed = value === day;
                        return (
                            <button
                                key={days}
                                type="button"
                                disabled={disabled}
                                aria-pressed={pressed}
                                onClick={() => onChange(day)}
                                className={chipClass(pressed)}
                            >
                                {presetLabel(days)}
                            </button>
                        );
                    })}
                </div>
            )}

            {help && !error && <p id={`${id}-help`} className={helpClass}>{help}</p>}
            {error && <p id={`${id}-error`} className={errorClass} role="alert">{error}</p>}

            {open && trigger.current && (
                <Calendar
                    anchor={trigger.current}
                    label={label}
                    selected={selected}
                    today={today}
                    earliest={notBefore ? fromIso(notBefore) : undefined}
                    onPick={day => { onChange(toIso(day)); setOpen(false); refocus(); }}
                    onClose={back => { setOpen(false); if (back) refocus(); }}
                />
            )}
        </div>
    );
}

/** The calendar itself: a layer of its own, placed against the field that opened it. */
function Calendar({
    anchor, label, selected, today, earliest, onPick, onClose,
}: {
    anchor: HTMLElement;
    label: string;
    selected: Date | undefined;
    today: Date;
    earliest: Date | undefined;
    onPick: (day: Date) => void;
    /** `refocus` is false when somebody pressed somewhere else, and focus belongs there. */
    onClose: (refocus: boolean) => void;
}) {
    const isPhone = useIsPhone();
    const panel = useModal<HTMLDivElement>(true, () => onClose(true), { closeOnEscape: true });
    const titleId = useId();
    const [place, setPlace] = useState<{ top: number; left: number } | null>(null);

    // Placed below the field, or above it when there is no room; kept there as the page scrolls.
    useLayoutEffect(() => {
        if (isPhone) return;
        const measure = () => {
            const box = anchor.getBoundingClientRect();
            const height = panel.current?.offsetHeight ?? 380;
            const width = panel.current?.offsetWidth ?? 300;
            const below = window.innerHeight - box.bottom >= height + 8 || box.top < height + 8;
            const top = below ? box.bottom + 4 : box.top - height - 4;
            const left = Math.min(Math.max(8, box.left), window.innerWidth - width - 8);
            setPlace({ top, left });
        };
        measure();
        window.addEventListener('scroll', measure, true);
        window.addEventListener('resize', measure);
        return () => {
            window.removeEventListener('scroll', measure, true);
            window.removeEventListener('resize', measure);
        };
    }, [anchor, isPhone, panel]);

    // A press anywhere else closes it.
    useEffect(() => {
        const onDown = (e: PointerEvent) => {
            const t = e.target as Node;
            if (!panel.current?.contains(t) && !anchor.contains(t)) onClose(false);
        };
        document.addEventListener('pointerdown', onDown);
        return () => document.removeEventListener('pointerdown', onDown);
    }, [anchor, onClose, panel]);

    // Inside a dialog it stays inside the dialog; anywhere else it sits over the page.
    const host = anchor.closest<HTMLElement>('[role="dialog"], [role="alertdialog"]') ?? document.body;

    return createPortal(
        <>
            {isPhone && <div className="fixed inset-0 z-70 bg-overlay animate-fade" aria-hidden="true" />}
            <div
                ref={panel}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                style={isPhone ? undefined : { top: place?.top ?? -9999, left: place?.left ?? -9999 }}
                className={cx(
                    'z-70',
                    isPhone
                        ? 'fixed inset-x-0 bottom-0 bg-card border-t border-separator shadow-e3 rounded-t-dialog px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] animate-sheet'
                        : cx('fixed w-max p-3', popoverClass),
                )}
            >
                <p id={titleId} className="sr-only">Choose a day for {label.toLowerCase()}</p>
                <Suspense
                    fallback={
                        // The grid's own size (seven 36px days by six weeks and the month line), so nothing jumps when it arrives.
                        <div className="h-72 w-63 grid place-items-center text-label-3" role="status" aria-label="Loading the calendar">
                            <Spinner />
                        </div>
                    }
                >
                    <DayGrid selected={selected} today={today} earliest={earliest} onPick={onPick} />
                </Suspense>
                <div className="flex items-center justify-between gap-2 pt-2 mt-1 border-t border-separator">
                    <button
                        type="button"
                        disabled={Boolean(earliest && startOfDay(today) < earliest)}
                        onClick={() => onPick(startOfDay(today))}
                        className="h-8 px-2.5 rounded-control text-secondary font-semibold text-accent-text hover:bg-hover disabled:opacity-50"
                    >
                        Today
                    </button>
                    <button
                        type="button"
                        onClick={() => onClose(true)}
                        className="h-8 px-2.5 rounded-control text-secondary font-semibold text-label-2 hover:bg-hover"
                    >
                        Close
                    </button>
                </div>
            </div>
        </>,
        host,
    );
}

const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** A calendar day from its `YYYY-MM-DD`, at local midnight — never UTC midnight, which is yesterday west of Greenwich. */
function fromIso(iso: string): Date {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y!, (m ?? 1) - 1, d ?? 1);
}

function toIso(day: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
}

function longDay(day: Date, locale: string): string {
    try {
        return new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }).format(day);
    } catch {
        return day.toDateString();
    }
}
